import {
    LIBRARY_CATEGORY,
    LIBRARY_STORAGE_KEY,
    libraryItemsForCategory,
    normalizeLibraryState,
    readLibraryState,
    reduceLibrary,
    writeLibraryState,
} from '../services/library.js'
import { mediaReference } from '../utils/media.js'
import { getMediaProvider, getMediaSearchChannels } from '../providers/registry.js'
import { fetchAnimeTrailersByShikimoriId } from '../services/welcomeTrailers.js'

export const LIBRARY_DETAILS_TTL_MS = 24 * 60 * 60 * 1000
export const LIBRARY_TRAILER_TTL_MS = 6 * 60 * 60 * 1000
export const LIBRARY_EMPTY_TRAILER_TTL_MS = 30 * 60 * 1000
export const LIBRARY_PREVIEW_VERSION = 3

let storageHandler = null
let activityHandler = null
let clockTimer = null

function commitAction(ctx, action) {
    ctx.commit('APPLY_LIBRARY_ACTION', action)
    writeLibraryState(ctx.state)
}

function providersForReference(reference) {
    const channelId = reference.kind === 'anime' ? 'anime' : 'cinema'
    const channel = getMediaSearchChannels().find((candidate) => candidate.id === channelId)
    const ids = (channel ? channel.providerIds : [reference.provider]).filter(Boolean)
    return [...new Set(ids)].map(getMediaProvider).filter(Boolean)
}

async function resolveWithProvider(provider, reference) {
    if (typeof provider.resolve === 'function') return provider.resolve(reference)
    if (provider.id === reference.provider && reference.providerId && typeof provider.getDetails === 'function') {
        return provider.getDetails(reference.providerId)
    }
    if (
        reference.source === 'provider'
        && reference.provider === provider.id
        && typeof provider.getDetails === 'function'
    ) {
        return provider.getDetails(reference.id)
    }
    return null
}

function shouldRefreshTrailer(media, shikimoriId, now, force) {
    if (!shikimoriId) return false
    const trailerAge = now - Number(media.trailerCheckedAt || 0)
    const trailerTtl = media.trailer
        ? LIBRARY_TRAILER_TTL_MS
        : LIBRARY_EMPTY_TRAILER_TTL_MS
    return force
        || !media.trailerCheckedAt
        || trailerAge < 0
        || trailerAge >= trailerTtl
}

export async function resolveLibraryMediaDetails(reference) {
    for (const provider of providersForReference(reference)) {
        try {
            const media = await resolveWithProvider(provider, reference)
            if (media && media.kind === reference.kind) return media
        } catch (_) {
            // Optional enrichment continues through the configured providers.
        }
    }
    return null
}

export default {
    state: () => ({ ...readLibraryState(), clock: Date.now() }),
    getters: {
        get_library_state: (state) => state,
        get_library_item: (state) => (key) => state.items[key] || null,
        get_library_favorites: (state) => libraryItemsForCategory(state, LIBRARY_CATEGORY.FAVORITES, state.clock),
        get_library_watch_later: (state) => libraryItemsForCategory(state, LIBRARY_CATEGORY.WATCH_LATER, state.clock),
        get_library_continue: (state) => libraryItemsForCategory(state, LIBRARY_CATEGORY.CONTINUE, state.clock),
        get_library_completed: (state) => libraryItemsForCategory(state, LIBRARY_CATEGORY.COMPLETED, state.clock),
        get_library_abandoned: (state) => libraryItemsForCategory(state, LIBRARY_CATEGORY.ABANDONED, state.clock),
        get_library_for_you: (state) => [
            ...libraryItemsForCategory(state, LIBRARY_CATEGORY.CONTINUE, state.clock)
                .map((record) => ({ ...record, displayCategory: LIBRARY_CATEGORY.CONTINUE })),
            ...libraryItemsForCategory(state, LIBRARY_CATEGORY.WATCH_LATER, state.clock)
                .map((record) => ({ ...record, displayCategory: LIBRARY_CATEGORY.WATCH_LATER })),
        ],
    },
    actions: {
        init_library({ commit }) {
            commit('REPLACE_LIBRARY_STATE', readLibraryState())
            if (typeof window === 'undefined' || storageHandler) return
            storageHandler = (event) => {
                if (event.key !== LIBRARY_STORAGE_KEY) return
                try {
                    commit('REPLACE_LIBRARY_STATE', event.newValue
                        ? normalizeLibraryState(JSON.parse(event.newValue))
                        : normalizeLibraryState(null))
                } catch (_) {
                    commit('REPLACE_LIBRARY_STATE', normalizeLibraryState(null))
                }
            }
            window.addEventListener('storage', storageHandler)
            activityHandler = () => {
                if (!document.hidden) commit('REFRESH_LIBRARY_CLOCK')
            }
            window.addEventListener('focus', activityHandler)
            document.addEventListener('visibilitychange', activityHandler)
            clockTimer = window.setInterval(() => commit('REFRESH_LIBRARY_CLOCK'), 60 * 60 * 1000)
        },
        dispose_library() {
            if (typeof window === 'undefined') return
            if (storageHandler) window.removeEventListener('storage', storageHandler)
            if (activityHandler) {
                window.removeEventListener('focus', activityHandler)
                document.removeEventListener('visibilitychange', activityHandler)
            }
            if (clockTimer) window.clearInterval(clockTimer)
            storageHandler = null
            activityHandler = null
            clockTimer = null
        },
        reset_library({ commit }) {
            commit('REPLACE_LIBRARY_STATE', null)
        },
        toggle_library_favorite(ctx, media) {
            commitAction(ctx, { type: 'toggle-favorite', media })
        },
        set_library_favorite(ctx, { media, value }) {
            commitAction(ctx, { type: 'set-favorite', media, value })
        },
        toggle_library_watch_later(ctx, media) {
            commitAction(ctx, { type: 'toggle-watch-later', media })
        },
        set_library_watch_later(ctx, { media, value }) {
            commitAction(ctx, { type: 'set-watch-later', media, value })
        },
        enrich_library_media(ctx, media) {
            commitAction(ctx, { type: 'enrich-media', media })
        },
        async hydrate_library_records(ctx, payload = {}) {
            const options = Array.isArray(payload) ? { records: payload } : (payload || {})
            const records = Array.isArray(options.records)
                ? options.records
                : Object.values(ctx.state.items)
            const force = options.force === true
            const requestedNow = Number(options.now)
            const now = Number.isFinite(requestedNow) && requestedNow > 0
                ? requestedNow
                : Date.now()
            const seen = new Set()
            const enriched = []

            /* Provider calls stay serial to avoid a request burst. They bypass
               the global Media details state so foreground navigation is never
               cancelled or replaced by background library enrichment. */
            for (const candidate of records) {
                const candidateKey = candidate && (candidate.key || candidate.media && candidate.media.key)
                const record = candidateKey && ctx.state.items[candidateKey]
                    || (candidate && candidate.media ? candidate : null)
                if (!record || !record.media || seen.has(record.key)) continue
                seen.add(record.key)

                const detailsAge = now - Number(record.media.detailsHydratedAt || 0)
                const needsDetails = force
                    || !record.media.detailsHydrated
                    || !record.media.detailsHydratedAt
                    || record.media.previewHydratedVersion !== LIBRARY_PREVIEW_VERSION
                    || detailsAge < 0
                    || detailsAge >= LIBRARY_DETAILS_TTL_MS
                const shikimoriId = record.media.kind === 'anime'
                    ? record.media.ids?.shikimori
                    : null
                const needsTrailer = shouldRefreshTrailer(record.media, shikimoriId, now, force)
                if (!needsDetails && !needsTrailer) continue

                const reference = mediaReference(record.media)
                if (!reference.id) continue

                let hydrated = null
                if (needsDetails) {
                    try {
                        const details = await resolveLibraryMediaDetails({ ...reference, snapshot: record.media })
                        if (details) {
                            hydrated = {
                                ...record.media,
                                ...details,
                                ids: {
                                    ...record.media.ids,
                                    ...Object.fromEntries(Object.entries(details.ids || {})
                                        .filter(([, value]) => value !== null && value !== undefined && value !== '')),
                                },
                                detailsHydrated: true,
                                detailsHydratedAt: now,
                                previewHydratedVersion: LIBRARY_PREVIEW_VERSION,
                                ...(details.trailer ? { trailerCheckedAt: now } : {}),
                            }
                        }
                    } catch (_) {
                        // Failed metadata is retried because no timestamp is written.
                    }
                }

                const hydratedShikimoriId = (hydrated || record.media).kind === 'anime'
                    ? (hydrated || record.media).ids?.shikimori
                    : null
                const needsHydratedTrailer = shouldRefreshTrailer(
                    hydrated || record.media,
                    hydratedShikimoriId,
                    now,
                    force
                )
                if (needsHydratedTrailer) {
                    try {
                        const trailerCandidates = await fetchAnimeTrailersByShikimoriId(hydratedShikimoriId)
                        hydrated = {
                            ...(hydrated || record.media),
                            ...(trailerCandidates.length ? {
                                trailer: trailerCandidates[0],
                                trailerCandidates,
                            } : {}),
                            trailerCheckedAt: now,
                        }
                    } catch (_) {
                        // Network failures are never negative-cached.
                    }
                }

                if (hydrated) {
                    commitAction(ctx, { type: 'enrich-media', media: hydrated })
                    enriched.push(hydrated)
                }
            }
            return enriched
        },
        start_library_playback(ctx, media) {
            commitAction(ctx, { type: 'start-playback', media })
        },
        update_library_progress(ctx, { media, progress }) {
            commitAction(ctx, { type: 'update-progress', media, progress })
        },
        complete_library_title(ctx, media) {
            commitAction(ctx, { type: 'complete', media })
        },
        remove_library_category(ctx, { key, category }) {
            commitAction(ctx, { type: 'remove-category', key, category })
        },
    },
    mutations: {
        REPLACE_LIBRARY_STATE(state, value) {
            const normalized = normalizeLibraryState(value)
            state.version = normalized.version
            state.items = normalized.items
            state.clock = Date.now()
        },
        APPLY_LIBRARY_ACTION(state, action) {
            const next = reduceLibrary(state, action)
            state.version = next.version
            state.items = next.items
            state.clock = Date.now()
        },
        REFRESH_LIBRARY_CLOCK(state) {
            state.clock = Date.now()
        },
    },
}
